// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {
	MANAGE_USER_FORM_FIELDS,
	useListUser,
} from '@dealstack/frontend/admin/pages/Manage/User/ListUser/useListUser';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { DataUserTct } from '@dealstack/shared/lib/generated';
import { Button, Flex, Grid, SegmentedControl, Space, Text } from '@mantine/core';
import { ColumnDef, createColumnHelper, flexRender, Row } from '@tanstack/react-table';
import { memo, ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { GODMODE_ROOT_ROUTES } from '@dealstack/frontend/admin/pages/Routing/Routing.tsx';
import styled from 'styled-components';

interface Props {
	className?: string;
}
const ListUser = ({ className }: Props) => {
	const manageInvestorsController = useListUser();
	const { tableContainerRef, rows, tanstackTable, rowVirtualizer } = manageInvestorsController;

	const totalSize = rowVirtualizer.getTotalSize();
	const virtualRows = rowVirtualizer.getVirtualItems();

	const paddingTop = virtualRows?.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
	const paddingBottom =
		virtualRows?.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;
	return (
		<div className={className}>
			<br />
			<FormProvider {...manageInvestorsController.manageInvestorsForm}>
				<form
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onSubmit={manageInvestorsController.manageInvestorsForm.handleSubmit(() => {})}
				>
					<SearchBox>
						<StringInput
							label="Search query"
							name={MANAGE_USER_FORM_FIELDS.SEARCH_QUERY}
						/>
					</SearchBox>
				</form>
			</FormProvider>
			<Grid gutter="sm">
				<Grid.Col>
					<Flex gap="1rem">
						<Button
							onClick={manageInvestorsController.downloadXLSX}
							size="md"
							variant="outline"
						>
							Download XLSX
						</Button>
					</Flex>
				</Grid.Col>
				<Grid.Col>
					<Flex gap="1rem">
						<Button
							onClick={manageInvestorsController.getUsersRequest.refetch}
							size="md"
							variant="outline"
						>
							Refresh
						</Button>
						<Flex align="center" direction="row" gap="xs">
							<Text>Results</Text>
							<SegmentedControl
								onChange={(value) => {
									manageInvestorsController.manageInvestorsForm.setValue(
										MANAGE_USER_FORM_FIELDS.RESULT_LIMIT,
										value
									);
								}}
								size="sm"
								data={[
									{ label: '20', value: '20' },
									{ label: '100', value: '100' },
									{ label: '500', value: '500' },
									{ label: 'Unlimited', value: 'infinite' },
								]}
							/>
						</Flex>
					</Flex>
				</Grid.Col>
			</Grid>
			<Space h="1rem" />
			<TableWrapper ref={tableContainerRef}>
				{manageInvestorsController.getUsersRequest?.isFetching ? <InlineLoader /> : null}
				<table className="table">
					<thead>
						{tanstackTable.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<th
											key={header.id}
											colSpan={header.colSpan}
											style={{ width: header.getSize() }}
										>
											{header.isPlaceholder ? null : (
												<div
													{...{
														className: header.column.getCanSort()
															? 'cursor-pointer clickAnimation select-none'
															: '',
														onClick:
															header.column.getToggleSortingHandler(),
													}}
												>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
													{{
														asc: ' 🔼',
														desc: ' 🔽',
													}[header.column.getIsSorted() as string] ??
														null}
												</div>
											)}
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody>
						{paddingTop > 0 && (
							<tr>
								<td style={{ height: `${paddingTop}px` }} />
							</tr>
						)}
						{rowVirtualizer.getVirtualItems().map((virtualRow) => {
							const row = rows[virtualRow.index] as Row<DataUserTct>;
							return (
								<tr key={row.id}>
									{row.getVisibleCells().map((cell) => {
										return (
											<td key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
						{paddingBottom > 0 && (
							<tr>
								<td style={{ height: `${paddingBottom}px` }} />
							</tr>
						)}
					</tbody>
				</table>
			</TableWrapper>
			<br />
			<div>{tanstackTable.getRowModel().rows.length} Rows</div>
		</div>
	);
};

const columnHelper = createColumnHelper<DataUserTct>();

export const manageInvestorsColumns = (): ColumnDef<DataUserTct, ReactNode>[] => {
	return [
		{
			id: 'dealstackId',
			size: 100,
			accessorFn: (row) => row.id,
			header: () => (
				<CompactText elipsis bold variant="button">
					Dealstack Id
				</CompactText>
			),
			cell: (info) => (
				<CompactText elipsis variant="caption">
					{info.getValue()}
				</CompactText>
			),
		},
		{
			id: 'email',
			accessorFn: (row) => row.email,
			cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			header: () => <span className="support-app-compact-text">Email</span>,
		},
		{
			id: 'active',
			size: 50,
			accessorFn: (row) => row.active,
			cell: (info) => <SupportAppBooleanStatus value={info.getValue() as boolean} />,
			header: () => <span className="support-app-compact-text">Active</span>,
		},
		{
			id: 'firstName',
			size: 50,
			accessorFn: (row) => row.firstName,
			cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			header: () => <span className="support-app-compact-text">First Name</span>,
		},
		{
			id: 'lastName',
			size: 50,
			accessorFn: (row) => row.lastName,
			cell: (info) => <span className="support-app-compact-text">{info.getValue()}</span>,
			header: () => <span className="support-app-compact-text">Last Name</span>,
		},
		columnHelper.display({
			id: 'action',
			size: 500,
			header: () => <span className="support-app-compact-text">Actions</span>,
			cell: (info) => {
				return <ActionColumnWrapperMemoized info={info} />;
			},
		}),
	];
};

const SupportAppBooleanStatus = ({ value }) => <span>{value ? 'Yes' : 'No'}</span>;

const CompactText = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.4;
	display: block;
`;
const ActionColumnWrapperBase = ({ info }) => {
	return (
		<Flex gap="1rem">
			<Link
				to={`${GODMODE_ROOT_ROUTES.MANAGE_USER}/investor/personal-details/${info.row.original.id}`}
			>
				<button className="button is-link">Investor Personal Details</button>
			</Link>
			<Link to={`${GODMODE_ROOT_ROUTES.MANAGE_USER}/user/${info.row.original.id}`}>
				<button className="button is-link">User</button>
			</Link>
			<Link to={`${GODMODE_ROOT_ROUTES.MANAGE_USER}/investor/${info.row.original.id}`}>
				<button className="button is-link" disabled={!info.row.original}>
					Investor
				</button>
			</Link>
		</Flex>
	);
};

export const ActionColumnWrapperMemoized = memo(
	ActionColumnWrapperBase,
	(prevProps, nextProps) => prevProps.info.row.original.id === nextProps.info.row.original.id
);

const TableWrapper = styled.div`
	position: relative;
	border: 1px solid lightgray;
	height: 40rem;
	overflow: auto;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: 2px 4px;
		text-align: left;
	}

	td {
		padding: 6px;
	}
`;

const SearchBox = styled.div`
	display: flex;
	align-items: end;
`;

const Memoized = memo(ListUser);
const Styled = styled(Memoized)`
	${SearchBox} {
		margin-bottom: 2rem;
	}
`;

export { Styled as ListUser };
